import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { ShoppingCart, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

function ShoppingListPage() {
  const { planId } = useParams();
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPlan = async () => {
      try {
        const response = await axios.get(`${API}/week-plans/detail/${planId}`, { withCredentials: true });
        setPlan(response.data.plan);
      } catch (error) {
        console.error('Failed to fetch plan:', error);
        toast.error('Failed to load shopping list');
        navigate('/dashboard');
      } finally {
        setLoading(false);
      }
    };

    fetchPlan();
  }, [planId, navigate]);

  const toggleItem = async (index) => {
    try {
      const response = await axios.patch(
        `${API}/week-plans/${planId}/shopping/${index}`,
        {},
        { withCredentials: true }
      );
      
      const updatedList = [...plan.shopping_list];
      updatedList[index].checked = response.data.checked;
      setPlan({ ...plan, shopping_list: updatedList });
    } catch (error) {
      console.error('Failed to toggle item:', error);
      toast.error('Failed to update item');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  const totalCost = plan?.shopping_list?.reduce((sum, item) => sum + (item.price_estimate_eur || 0), 0) || 0;
  const checkedCount = plan?.shopping_list?.filter(item => item.checked).length || 0;

  return (
    <div className="min-h-screen bg-[#FDFBF7] py-4 sm:py-8">
      <div className="container mx-auto px-3 sm:px-4 max-w-4xl">
        <Button
          variant="ghost"
          onClick={() => navigate('/dashboard')}
          className="mb-4 sm:mb-6"
          data-testid="back-to-dashboard-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>

        <div className="flex items-center gap-3 mb-4 sm:mb-8">
          <div className="bg-secondary rounded-full p-2 sm:p-3">
            <ShoppingCart className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-[#2C3E2D]" data-testid="shopping-list-title">
              Shopping List
            </h1>
            <p className="text-sm sm:text-base text-gray-600">Week of {plan?.start_date}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 mb-4 sm:mb-8">
          <Card>
            <CardContent className="pt-6">
              <p className="text-sm text-gray-600 mb-1">Total Items</p>
              <p className="text-3xl font-bold text-primary" data-testid="total-items">
                {plan?.shopping_list?.length || 0}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <p className="text-sm text-gray-600 mb-1">Checked Off</p>
              <p className="text-3xl font-bold text-success" data-testid="checked-count">
                {checkedCount}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <p className="text-sm text-gray-600 mb-1">Estimated Cost</p>
              <p className="text-3xl font-bold text-secondary" data-testid="total-cost">
                €{totalCost.toFixed(2)}
              </p>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Items to Buy</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {plan?.shopping_list?.map((item, index) => (
                <label
                  key={index}
                  className={`flex items-center gap-4 p-4 rounded-lg cursor-pointer transition-all ${
                    item.checked ? 'bg-gray-100 opacity-60' : 'bg-white hover:bg-gray-50'
                  }`}
                  data-testid={`shopping-item-${index}`}
                >
                  <Checkbox
                    checked={item.checked || false}
                    onCheckedChange={() => toggleItem(index)}
                  />
                  <div className="flex-1">
                    <p className={`font-medium ${item.checked ? 'line-through text-gray-500' : 'text-[#2C3E2D]'}`}>
                      {item.product_name}
                    </p>
                    <p className="text-sm text-gray-600">
                      {item.amount} {item.unit}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className={`font-semibold ${item.checked ? 'text-gray-400' : 'text-secondary'}`}>
                      €{item.price_estimate_eur?.toFixed(2)}
                    </p>
                  </div>
                </label>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default ShoppingListPage;
